import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    marginRight: 0,
    background: 'transparent',
    border: 0,
    boxShadow: 'none',
    backdropFilter: 'none',
    borderRadius: 0,
    transition: theme.transitions.create(
        'margin',
        open
            ? {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen
              }
            : {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen
              }
    ),
    [theme.breakpoints.up('lg')]: {
        marginLeft: open ? 0 : -(drawerWidth - 20),
        width: `calc(100% - ${drawerWidth}px)`
    },
    [theme.breakpoints.between('md', 'lg')]: {
        marginLeft: open ? 0 : -(drawerWidth - 20),
        width: `calc(100% - ${drawerWidth}px)`,
        padding: '16px'
    },
    [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
        padding: '12px'
    },
    [theme.breakpoints.down('sm')]: {
        marginLeft: '0',
        width: '100%',
        padding: '8px'
    }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));

    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', flex: 1 }}>
                <CssBaseline />
                {/* header */}
                <AppBar
                    enableColorOnDark
                    position="fixed"
                    color="inherit"
                    elevation={0}
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.72)',
                        backdropFilter: 'blur(24px)',
                        transition: leftDrawerOpened ? theme.transitions.create('width') : 'none',
                        zIndex: theme.zIndex.drawer + 1,
                        borderBottom: '1px solid',
                        borderColor: 'rgba(15, 140, 255, 0.12)',
                        boxShadow: '0 12px 40px rgba(7, 17, 31, 0.06)'
                    }}
                >
                    <Toolbar
                        sx={{
                            minHeight: matchDownSm ? '56px' : '64px',
                            px: matchDownSm ? 1 : 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1
                        }}
                    >
                        <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                    </Toolbar>
                </AppBar>

                {/* drawer */}
                <Sidebar drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

                {/* main content */}
                <Main theme={theme} open={leftDrawerOpened}>
                    {/* breadcrumb */}
                    <Box
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            mb: matchDownSm ? 1 : 2
                        }}
                    >
                        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    </Box>
                    <Outlet />
                </Main>
            </Box>
        </Box>
    );
};

export default MainLayout;
